import { useMemo } from "react";
import type { LectureType } from "@vlab-corporation/studiomate-api";
import { useStudiomateSchedules } from "@/hooks/useStudiomateSchedules";
import { useQuery } from "@tanstack/react-query";
import { getClassCompletionsBatch } from "@/services/firestore/classCompletions";
import { getBranchPolicy } from "@/services/firestore/branchPolicy";
import { getReRegistrations } from "@/services/firestore/members";
import { getJournalsByClassId } from "@/services/firestore/journals";
import { useSyncMembers } from "@/hooks/useSyncMembers";
import { sameStaffId } from "@/utils/staff-identity";

export interface PendingJournalClass {
  id: string;
  date: string;
  title: string;
  startTime: string;
  endTime: string;
  staffName: string;
  currentTrainees: number;
  maxTrainees: number;
  lectureType: LectureType;
  bookings: Array<{
    memberName: string;
    status: string;
    ticketTitle?: string;
    remainingCoupon?: number;
    /** 해당 회원권의 총 이용 횟수. N회차 계산에 사용: N = maxCoupon - remainingCoupon */
    maxCoupon?: number;
    member?: { id?: string } | null;
    /** Studiomate 회원 고유 ID. API가 반환하지 않을 수 있으므로 선택적 필드. */
    studiomateMemberId?: number;
  }>;
  journalWritten: boolean;
  isNoShow: boolean;
  /** 그룹 수업 일지 진척도 (그룹 수업에서만 존재) */
  groupProgress?: {
    /** 작성 완료된 일지 수 */
    written: number;
    /** active 수강생 수 (cancel 제외) */
    total: number;
  };
}

function toKSTDateString(date: Date): string {
  const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().substring(0, 10);
}

export function get30DayRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const endDate = toKSTDateString(now);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const startDate = toKSTDateString(thirtyDaysAgo);
  return { startDate, endDate };
}

/** yearMonth("YYYY-MM")의 1일~말일(이번달이면 오늘까지) 범위를 반환한다. */
export function getMonthRange(yearMonth: string): { startDate: string; endDate: string } {
  const [y, m] = yearMonth.split("-").map(Number);
  const startDate = `${y}-${String(m).padStart(2, "0")}-01`;
  const now = new Date();
  const today = toKSTDateString(now);
  const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  if (yearMonth === currentYm) {
    return { startDate, endDate: today };
  }
  // 해당 월 마지막 날
  const lastDay = new Date(y, m, 0).getDate();
  const endDate = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { startDate, endDate };
}

function isPastClass(date: string, startTime: string): boolean {
  const classDateTime = new Date(`${date}T${startTime}:00+09:00`);
  return classDateTime.getTime() < Date.now();
}

export function usePendingJournals(
  ownerUid: string,
  centerId: number,
  staffKey?: number | string,
  dateRange?: { startDate: string; endDate: string },
) {
  const range = useMemo(
    () => dateRange ?? { startDate: "2000-01-01", endDate: toKSTDateString(new Date()) },
    [dateRange?.startDate, dateRange?.endDate],
  );
  const {
    data: scheduleData,
    isLoading: schedulesLoading,
    error: schedulesError,
  } = useStudiomateSchedules(range, centerId);

  const { data: branchPolicy, isLoading: branchPolicyLoading } = useQuery({
    queryKey: ["branchPolicy", centerId],
    queryFn: () => getBranchPolicy(ownerUid, centerId),
    staleTime: 5 * 60 * 1000,
    enabled: !!ownerUid && !!centerId,
  });

  // 마운트 시 Studiomate 전체 회원 동기화 후 최신 회원 목록 반환
  const { membersMap, isLoading: membersLoading } = useSyncMembers(ownerUid, centerId);

  // 재등록 관계 맵 로드 (30분 stale)
  const { data: reRegistrations, isLoading: reRegistrationsLoading } = useQuery({
    queryKey: ["reRegistrations", ownerUid, centerId],
    queryFn: () => getReRegistrations(ownerUid, centerId),
    staleTime: 30 * 60 * 1000,
    enabled: !!ownerUid && !!centerId,
  });

  const journalWritingStartDate = branchPolicy?.journalWritingStartDate;

  /** 공통 필터 조건: 수강생 있고, 과거 수업이며, 강사 조건 및 시작일 조건을 충족 */
  function matchesBaseFilter(s: {
    currentTrainees: number;
    date: string;
    startTime: string;
    staffId: number | string;
    type: LectureType;
  }): boolean {
    return (
      s.currentTrainees > 0 &&
      isPastClass(s.date, s.startTime) &&
      (staffKey === undefined || sameStaffId(s.staffId, staffKey)) &&
      (!journalWritingStartDate || s.date >= journalWritingStartDate)
    );
  }

  // 전체 classId 목록 (개인 + 그룹 수업)
  const classIds = useMemo(() => {
    if (!scheduleData?.schedules) return [];
    return scheduleData.schedules.filter(matchesBaseFilter).map((s) => String(s.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleData, staffKey, journalWritingStartDate]);

  // 그룹 수업 classId 목록 (일지 진척도 조회용)
  const groupClassIds = useMemo(() => {
    if (!scheduleData?.schedules) return [];
    return scheduleData.schedules
      .filter((s) => s.type === "G" && matchesBaseFilter(s))
      .map((s) => String(s.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleData, staffKey, journalWritingStartDate]);

  const { data: completions, isLoading: completionsLoading } = useQuery({
    queryKey: ["classCompletions-batch", centerId, classIds],
    queryFn: () => getClassCompletionsBatch(ownerUid, centerId, classIds),
    enabled: classIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // 그룹 수업별 작성된 일지 memberId Set 조회 (classId → journal memberId Set)
  // studiomateMemberId(숫자 문자열) 또는 encodeURIComponent(memberName) 형식 혼재 가능
  const { data: groupJournalMemberIds, isLoading: groupJournalCountsLoading } = useQuery({
    queryKey: ["groupJournalCounts", ownerUid, centerId, groupClassIds],
    queryFn: async () => {
      const results = await Promise.all(
        groupClassIds.map((classId) => getJournalsByClassId(ownerUid, centerId, classId)),
      );
      return new Map(
        groupClassIds.map((id, i) => [
          id,
          new Set(results[i].map((j) => j.memberId).filter(Boolean) as string[]),
        ]),
      );
    },
    enabled: groupClassIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const allClasses = useMemo<PendingJournalClass[]>(() => {
    if (!scheduleData?.schedules) return [];

    return scheduleData.schedules.filter(matchesBaseFilter).map((s) => {
      const classId = String(s.id);
      const completion = completions?.get(classId);
      // cancel 상태 예약은 제외하고 member 매칭
      const enrichedBookings = s.bookings.map((b) => {
        if (b.status === "cancel") {
          return { ...b };
        }
        const member =
          b.studiomateMemberId != null
            ? membersMap?.get(String(b.studiomateMemberId))
            : membersMap?.get(b.memberName);
        return {
          ...b,
          member: member ? { id: member.id } : null,
        };
      });

      // 그룹 수업: groupProgress 계산 및 journalWritten 판정
      if (s.type === "G") {
        // active 수강생 목록 (cancel 상태 제외)
        const activeBookings = s.bookings.filter((b) => b.status !== "cancel");
        const journalIds = groupJournalMemberIds?.get(classId) ?? new Set<string>();
        // studiomateMemberId(숫자 문자열) 또는 encodeURIComponent(memberName)으로 매칭
        const writtenCount = activeBookings.filter((b) => {
          if (b.studiomateMemberId !== undefined && journalIds.has(String(b.studiomateMemberId)))
            return true;
          return journalIds.has(encodeURIComponent(b.memberName));
        }).length;
        const groupProgress = {
          written: writtenCount,
          total: activeBookings.length,
        };
        // 모든 active 수강생의 일지가 작성된 경우에만 완료로 판정
        const journalWritten = activeBookings.length > 0 && writtenCount === activeBookings.length;

        return {
          id: classId,
          date: s.date,
          title: s.title,
          startTime: s.startTime,
          endTime: s.endTime,
          staffName: s.staffName,
          currentTrainees: s.currentTrainees,
          maxTrainees: s.maxTrainees,
          lectureType: s.type,
          bookings: enrichedBookings,
          journalWritten,
          isNoShow: completion?.isNoShow ?? false,
          groupProgress,
        };
      }

      // 개인 수업: 기존 로직 그대로
      return {
        id: classId,
        date: s.date,
        title: s.title,
        startTime: s.startTime,
        endTime: s.endTime,
        staffName: s.staffName,
        currentTrainees: s.currentTrainees,
        maxTrainees: s.maxTrainees,
        lectureType: s.type,
        bookings: enrichedBookings,
        journalWritten: completion?.journalWritten ?? false,
        isNoShow: completion?.isNoShow ?? false,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scheduleData,
    completions,
    membersMap,
    groupJournalMemberIds,
    staffKey,
    journalWritingStartDate,
  ]);

  const pendingClasses = useMemo(() => allClasses.filter((c) => !c.journalWritten), [allClasses]);

  const completedClasses = useMemo(() => allClasses.filter((c) => c.journalWritten), [allClasses]);

  const completedCount = completedClasses.length;

  return {
    allClasses,
    pendingClasses,
    completedClasses,
    totalCount: allClasses.length,
    completedCount,
    pendingCount: pendingClasses.length,
    reRegistrations,
    journalWritingStartDate,
    isLoading:
      schedulesLoading ||
      completionsLoading ||
      branchPolicyLoading ||
      membersLoading ||
      reRegistrationsLoading ||
      groupJournalCountsLoading,
    error: schedulesError,
    apiError: scheduleData?.error,
  };
}
